import React, { useRef, useState } from "react";
import { Modal } from "../Modal";
import { ActionButton } from "../ActionButton";
import { Icons } from "../Icons";
import {
  SignaturePadCanvas,
  SignaturePadCanvasHandle,
} from "./SignaturePadCanvas";

interface SignatureFieldProps {
  label: string;
  value?: string;
  currentImage?: string;
  onChange: (base64: string) => void;
  onRemove?: () => void;
  disabled?: boolean;
  hint?: string;
}

export const SignatureField: React.FC<SignatureFieldProps> = ({
  label,
  value,
  currentImage,
  onChange,
  onRemove,
  disabled = false,
  hint,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const padRef = useRef<SignaturePadCanvasHandle>(null);
  const preview = value || currentImage || null;

  const handleOpen = () => {
    if (disabled) return;
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleSave = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      return;
    }

    onChange(pad.toDataUrl());
    setModalOpen(false);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      onChange("");
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
        {label}
      </label>

      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {preview && (
          <div className="relative shrink-0">
            <img
              src={preview}
              alt="Assinatura"
              className="h-20 w-40 object-contain rounded-lg border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-white"
            />
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Remover assinatura"
            >
              ×
            </button>
          </div>
        )}

        <div className="flex-1">
          <ActionButton
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpen}
            disabled={disabled}
            icon={<Icons.Edit sx={{ fontSize: 18 }} />}
          >
            {preview ? "Redesenhar assinatura" : "Desenhar assinatura"}
          </ActionButton>
          {hint && <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleClose}
        title="Desenhar assinatura"
        size="lg"
        headerGradient="blue"
      >
        <div className="p-4 sm:p-6 space-y-4">
          <SignaturePadCanvas
            key={modalOpen ? preview ?? "new" : "closed"}
            ref={padRef}
            initialImage={preview ?? undefined}
            disabled={disabled}
          />

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
            <ActionButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => padRef.current?.clear()}
              disabled={disabled}
            >
              Limpar
            </ActionButton>
            <ActionButton
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={disabled}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={disabled}
              icon={<Icons.Save sx={{ fontSize: 18 }} />}
            >
              Usar assinatura
            </ActionButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};
